// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson

import type { Bundle } from '../data';
import { GRADE_ORDER } from '../data';
import { num, pct1, safe, tipAttr, escapeHtml, signed } from '../format';

/**
 * The mechanism behind the Ladder view: not "the APS stopped hiring", but "the
 * door moved up". A ridgeline of thirteen overlapping filled series on a shared
 * time axis and a COMMON y-scale, junior at the top — so the junior masses
 * visibly deflate while the middle grades swell.
 *
 * Clicking a grade mirrors its separations below the baseline, which turns the
 * gap between the two lobes into net flow for that grade.
 */

export function renderIntake(root: HTMLElement, b: Bundle): void {
  const years = b.intake.engagements.years;
  let focus: string | null = null;

  root.innerHTML = `
    <div class="view-head">
      <h2>Where the door is</h2>
      <p>People engaged as ongoing employees each year, by the <span class="glossary-link" data-term="classification">grade</span> they were hired into. Click a ridge to mirror that grade's departures below the line and read net flow.</p>
    </div>
    <div class="callout" id="intake-callout"></div>
    <div class="panel">
      <div class="chart-wrap" id="ridge-wrap"></div>
      <div class="legend">
        <span class="legend-item"><span class="legend-swatch" style="background:#2d86ab"></span> Engagements</span>
        <span class="legend-item"><span class="legend-swatch" style="background:#a33b52"></span> Separations (shown when a grade is selected)</span>
        <span class="legend-item">All ridges share one vertical scale, so heights are comparable between grades.</span>
      </div>
    </div>`;

  const val = (which: 'engagements' | 'separations', grade: string, y: number): number => {
    const r = b.intake[which].rows.find((x) => x.label === grade);
    const v = r?.values[String(y)];
    return v === null || v === undefined ? 0 : v;
  };

  function draw(): void {
    const grades = GRADE_ORDER.filter((g) => b.intake.engagements.rows.some((r) => r.label === g));
    const max = Math.max(...grades.flatMap((g) => years.map((y) => val('engagements', g, y))), 1);
    const W = 900, L = 92, R = 20, T = 22;
    const lane = 46, ridgeH = 74;
    const H = T + grades.length * lane + 40;
    const iw = W - L - R;
    const xAt = (i: number) => L + (i / (years.length - 1)) * iw;

    const ridges = grades.map((g, gi) => {
      const baseY = T + gi * lane + lane;
      const yAt = (v: number) => baseY - (safe(v) / max) * ridgeH;
      const pts = years.map((y, i) => `${i ? 'L' : 'M'}${xAt(i).toFixed(1)},${yAt(val('engagements', g, y)).toFixed(1)}`).join('');
      const area = `${pts} L${xAt(years.length - 1).toFixed(1)},${baseY} L${L},${baseY} Z`;
      const dim = focus && focus !== g;
      const selected = focus === g;

      let sep = '';
      if (selected) {
        const sPts = years.map((y, i) => `${i ? 'L' : 'M'}${xAt(i).toFixed(1)},${(baseY + (safe(val('separations', g, y)) / max) * ridgeH).toFixed(1)}`).join('');
        sep = `<path d="${sPts} L${xAt(years.length - 1).toFixed(1)},${baseY} L${L},${baseY} Z"
                     fill="#a33b52" fill-opacity="0.5" stroke="#a33b52" stroke-width="1.4" />`;
      }

      const hot = years.map((y, i) => {
        const e = val('engagements', g, y);
        const s = val('separations', g, y);
        const yearTotal = grades.reduce((t, gg) => t + val('engagements', gg, y), 0);
        return `<rect class="bar" x="${(xAt(i) - iw / (years.length * 2)).toFixed(1)}" y="${baseY - ridgeH}"
          width="${(iw / years.length).toFixed(1)}" height="${ridgeH + (selected ? ridgeH : 0)}" fill="transparent"
          data-tip="${escapeHtml(tipAttr([
            `${g} · ${y}`,
            `Engaged: ${num(e)}`,
            `Separated: ${num(s)}`,
            `Net: ${signed(e - s)}`,
            `Share of that year's intake: ${pct1(yearTotal ? (e / yearTotal) * 100 : null)}`,
          ]))}"><title></title></rect>`;
      }).join('');

      return `<g class="${dim ? 'dimmed' : ''}" data-ridge="${escapeHtml(g)}" tabindex="0" role="button" style="cursor:pointer">
        <line class="gridline" x1="${L}" y1="${baseY}" x2="${W - R}" y2="${baseY}" />
        <path class="bar" d="${area}" fill="#2d86ab" fill-opacity="${selected ? 0.75 : 0.5}" stroke="#2d86ab" stroke-width="1.4" />
        ${sep}
        <text x="${L - 10}" y="${baseY - 4}" font-size="11.5" text-anchor="end" font-weight="${selected ? 700 : 550}">${escapeHtml(g)}</text>
        ${hot}
      </g>`;
    }).join('');

    root.querySelector('#ridge-wrap')!.innerHTML = `
      <svg class="chart" viewBox="0 0 ${W} ${H}" role="img" aria-label="Ongoing engagements by classification, ${years[0]} to ${years[years.length - 1]}">
        ${ridges}
        <text x="${L}" y="${H - 12}" font-size="11">${years[0]}</text>
        <text x="${W - R}" y="${H - 12}" font-size="11" text-anchor="end">${years[years.length - 1]}</text>
        <text x="${W - R}" y="14" font-size="11" text-anchor="end">peak ${num(max)} in a single grade-year</text>
      </svg>`;

    const first = years[0], last = years[years.length - 1];
    const totalFirst = grades.reduce((s, g) => s + val('engagements', g, first), 0);
    const totalLast = grades.reduce((s, g) => s + val('engagements', g, last), 0);
    const jFirst = ['APS 1', 'APS 2', 'APS 3'].reduce((s, g) => s + val('engagements', g, first), 0);
    const jLast = ['APS 1', 'APS 2', 'APS 3'].reduce((s, g) => s + val('engagements', g, last), 0);
    root.querySelector('#intake-callout')!.innerHTML =
      `The APS hired <strong>${num(totalFirst)}</strong> ongoing employees in ${first} and <strong>${num(totalLast)}</strong> in ${last} — `
      + `barely changed. What moved is <em>where</em> they enter: APS 1–3 was `
      + `<strong>${pct1(totalFirst ? (jFirst / totalFirst) * 100 : null)}</strong> of intake in ${first} and `
      + `<strong>${pct1(totalLast ? (jLast / totalLast) * 100 : null)}</strong> in ${last}. `
      + `The service did not stop recruiting; it stopped recruiting at the bottom.`;
  }

  root.addEventListener('click', (e) => {
    const g = (e.target as Element).closest('[data-ridge]');
    if (!g) return;
    const grade = g.getAttribute('data-ridge')!;
    focus = focus === grade ? null : grade;
    draw();
  });

  draw();
}
